/**
 * Polish copy for the five nervous-system regulation practices.
 *
 * REVIEWED BY MARTA STANKIEWICZ — Drive doc 06, returned 2026-08-29.
 * She rewrote 54 fields, including the whole affirmations set, which is her own
 * authored ritual and credited to her by name in the app.
 *
 * ⚠ SHE ALSO SOFTENED THE CLINICAL CLAIMS, AND THE ENGLISH STILL HAS THEM.
 * The English asserts specific physiological outcomes the app cannot verify —
 * "Your cortisol is lower", "Your nervous system just registered safety",
 * "Directly activates the ventral vagal", "one of the most evidence-backed
 * regulation tools available". Her Polish consistently rewrites these as
 * possibility rather than fact ("może wspierać…", "otrzymał chwilę na
 * zauważenie…") and replaces the outcome claims with an invitation to notice.
 *
 * That is a clinical judgement, not a translation choice, and it means the two
 * languages currently make different promises about what these practices do.
 * Flagged for Andy and Sage: the English probably needs the same treatment.
 *
 * GENDERED FORMS
 * Step instructions are imperatives, which are naturally neutral in Polish. The
 * affirmations are first person and were the risk: "I'm sorry I left you"
 * wants "zostawiłem/zostawiłam". Recast to the act rather than the actor
 * ("Przepraszam za to odejście"), so nothing here assumes a gender.
 *
 * NOT LOCALIZED
 * durationSeconds, durationMinutes, colors, stepCount, isSaveable,
 * isBreathPrompt and freeTierStepCount are all behaviour or layout. Only what a
 * user reads is swapped.
 */
import type { Preparation, PreparationId } from './preparations'

export interface PreparationStepPl {
  instruction: string
  cue?: string
  category?: string
}

export interface PreparationPl {
  name: string
  tagline: string
  description: string
  regulation: string
  completionLine: string
  attribution?: string
  /** Parallel to steps — same order, same length. */
  steps: PreparationStepPl[]
}

export const PREPARATIONS_PL: Record<PreparationId, PreparationPl> = {
  orienting: {
    name: 'Orientowanie się w przestrzeni',
    tagline: 'Powróć do chwili obecnej poprzez kontakt z otoczeniem.',
    description: 'Układ nerwowy nieustannie odbiera informacje z otoczenia i ocenia, co dzieje się wokół Ciebie. Ta krótka praktyka zaprasza Cię do świadomego rozejrzenia się, zauważenia przestrzeni i powrotu uwagą do tego, co jest obecne właśnie teraz. Zajmuje około dwóch minut.',
    regulation: 'Orientowanie się w otoczeniu może wspierać poczucie obecności, kontaktu i regulacji poprzez kierowanie uwagi na sygnały płynące z aktualnego otoczenia.',
    completionLine: 'Twój układ nerwowy otrzymał chwilę na zauważenie tego, co jest obecne tutaj i teraz.',
    steps: [
      { instruction: 'Znajdź wygodną pozycję — siedzącą lub stojącą. Pozwól dłoniom swobodnie spocząć.', cue: 'Dotknij, gdy poczujesz gotowość.' },
      { instruction: 'Weź jeden spokojny oddech. Poczuj kontakt stóp z podłogą. Jeśli siedzisz, zauważ również ciężar ciała opierającego się o siedzisko.', cue: 'Po prostu bądź tutaj.' },
      { instruction: 'Pozwól spojrzeniu powoli wędrować po przestrzeni wokół Ciebie. Po prostu zauważaj to, co pojawia się w polu widzenia.', cue: 'Pozwól wzrokowi spocząć tam, gdzie zechce.' },
      { instruction: 'Kiedy coś przyciągnie Twoją uwagę, zatrzymaj tam spojrzenie na chwilę. Zauważ kolor. Kształt. Fakturę.', cue: 'Ta chwila może być po prostu chwilą obecności tutaj.' },
      { instruction: 'Powoli skieruj uwagę z powrotem do siebie. Weź spokojny oddech. Zauważ, jak czuje się teraz Twoje ciało i czy coś zmieniło się od początku praktyki.', cue: 'To wszystko. To właśnie jest ta praktyka.' },
    ],
  },

  'coherent-heart': {
    name: 'Oddech Serca',
    tagline: 'Połącz oddech z łagodnym, równym rytmem.',
    description: 'Powolny, równomierny oddech może wspierać regulację układu nerwowego i pomagać ciału przechodzić w spokojniejszy, bardziej obecny stan. Przez kilka minut pozwól oddechowi płynąć w łagodnym, równym rytmie i zauważaj, jak odpowiada Twoje ciało.',
    regulation: 'Powolne, rytmiczne oddychanie może wspierać procesy związane z regulacją autonomicznego układu nerwowego i sprzyjać większemu poczuciu spokoju oraz obecności.',
    completionLine: 'Zatrzymaj się na chwilę i zauważ, jak Twój oddech, ciało i wewnętrzny stan czują się teraz.',
    steps: [
      { instruction: 'Usiądź wygodnie. Jeśli masz ochotę, połóż jedną dłoń na klatce piersiowej.', cue: 'Dotknij, by rozpocząć rytm oddechu.' },
      { instruction: 'Weź spokojny wdech, licząc do 5… Następnie wykonaj spokojny wydech, również licząc do 5…', cue: 'Wdech... 2... 3... 4... 5... Wydech... 2... 3... 4... 5...' },
      { instruction: 'Pozwól teraz oddechowi powrócić do jego naturalnego rytmu. Obserwuj go takim, jaki jest.', cue: 'Zauważ tę ciszę.' },
      { instruction: 'Zanim przejdziesz dalej, zatrzymaj się na chwilę. Zauważ swój oddech, ciało i jakość swojego wewnętrznego stanu.', cue: 'Zauważ, czy coś w ciele się przesunęło.' },
    ],
  },

  toning: {
    name: 'Tonowanie głosem',
    tagline: 'Użyj głosu i wibracji, aby wspierać wyciszenie i obecność.',
    description: 'Nucenie i wydłużone dźwięki tworzą delikatną wibrację odczuwaną w gardle, klatce piersiowej i ciele. Dla wielu osób taki sposób pracy z głosem może wspierać wyciszenie, kontakt z ciałem i poczucie większej obecności. Ta praktyka zajmuje około trzech minut.',
    regulation: 'Wydłużony wydech połączony z dźwiękiem może wspierać regulację poprzez oddech, wibrację i świadomy kontakt z ciałem.',
    completionLine: 'Pozwól sobie przez chwilę poczuć ciszę i rezonans, który pozostał po dźwięku.',
    steps: [
      { instruction: 'Znajdź miejsce, w którym możesz swobodnie wydawać dźwięk i czuć się wystarczająco komfortowo, aby usłyszeć własny głos.', cue: 'Dotknij, gdy poczujesz gotowość.' },
      { instruction: 'Połóż jedną dłoń na klatce piersiowej i weź spokojny wdech.', cue: 'Poczuj, jak dłoń się unosi.' },
      { instruction: 'Na wydechu pozwól pojawić się łagodnemu „mmm”. Wydawaj dźwięk nisko, miękko i swobodnie, pozwalając mu płynąć bez wysiłku.', cue: 'Poczuj wibrację w klatce piersiowej i gardle.' },
      { instruction: 'Pozwól dźwiękowi zmieniać się naturalnie. Może przejść w „aaa”, „ooo” albo pozostać przy „mmm”. Podążaj za tym, co w tej chwili wydaje się najbardziej swobodne.', cue: 'Nie da się tego zrobić źle.' },
      { instruction: 'Pozwól teraz pojawić się ciszy. Zostań przez chwilę z odczuciem rezonansu w ciele i zauważ, co jest obecne.', cue: 'Zauważ, czy coś jest inne.' },
    ],
  },

  shake: {
    name: 'Otrząsanie ciała',
    tagline: 'Pozwól ciału poruszyć napięcie poprzez swobodny ruch.',
    description: 'Napięcie może być odczuwane w różnych częściach ciała — w barkach, szczęce, klatce piersiowej, brzuchu czy biodrach. Świadome potrząsanie ciałem wprowadza ruch w miejsca, które mogą być napięte lub usztywnione. Pozwól ciału poruszać się swobodnie i zauważaj, jak odpowiada na tę praktykę.',
    regulation: 'Swobodny, świadomy ruch może pomóc zauważyć i rozluźnić napięcie w ciele, jednocześnie wspierając kontakt z oddechem, ruchem i odczuciami płynącymi z ciała.',
    completionLine: 'Zatrzymaj się na chwilę i zauważ, jak Twoje ciało czuje się po ruchu.',
    steps: [
      { instruction: 'Jeśli możesz, stań albo usiądź w miejscu, które daje Ci swobodę ruchu. Daj sobie chwilę na oswojenie się z tą praktyką.', cue: 'Dotknij, gdy poczujesz gotowość.' },
      { instruction: 'Zacznij od dłoni. Potrząsaj nimi lekko i swobodnie, jak podczas strzepywania kropli wody.', cue: 'Luźno. Szybko. Niech nadgarstki będą miękkie.' },
      { instruction: 'Pozwól, aby ruch stopniowo przeszedł przez dłonie do ramion i barków.', cue: 'Nie kontroluj tego — pozwól ruchowi znaleźć własną drogę.' },
      { instruction: 'Jeśli masz ochotę, pozwól ruchowi objąć górną część ciała, biodra, nogi — tyle ciała, ile w tej chwili chce się poruszać. Daj sobie swobodę poruszania się po swojemu.', cue: 'Nie da się tego zrobić źle.' },
      { instruction: 'Teraz pozwól ruchowi stopniowo zwolnić, aż ciało zatrzyma się w bezruchu.', cue: 'Zamknij oczy. Co zauważasz?' },
      { instruction: 'Weź spokojny oddech. Zauważ odczucia w ciele i pozwól sobie przez chwilę z nimi pozostać.', cue: 'Zauważ, czy coś się zmieniło.' },
    ],
  },

  affirmations: {
    name: 'Afirmacje Wybaczenia Sobie',
    tagline: 'Słowa, które pomagają Ci wracać do siebie z czułością.',
    description: 'Pięć obszarów afirmacji prowadzi Cię przez emocje, ciało, kontakt z młodszą częścią siebie, odzyskiwanie wewnętrznej mocy oraz głębsze poczucie połączenia ze sobą. Przechodź przez nie powoli. Zatrzymuj się przy tych słowach, które poruszają Cię najbardziej, i pozwól im wybrzmiewać w swoim tempie.',
    regulation: 'Te afirmacje łączą słowa z odczuwaniem w ciele i świadomą obecnością. Ich celem jest pogłębianie kontaktu ze sobą, własnymi emocjami i wewnętrzną prawdą.',
    attribution: 'W opracowaniu Marty Stankiewicz · Awaken By Love',
    completionLine: 'Te słowa mogą od tej chwili stać się częścią Twojego wewnętrznego języka.',
    steps: [
      { instruction: 'Mam prawo czuć to, co pojawia się we mnie w tej chwili.', category: 'Emocje' },
      { instruction: 'Moje emocje niosą informacje o tym, czego potrzebuję, czego pragnę i co jest dla mnie ważne.', category: 'Emocje' },
      { instruction: 'Moje ciało pamiętało to, czego przez długi czas brakowało słów, aby wyrazić.', category: 'Ciało' },
      { instruction: 'Wracam do swojego ciała z czułością, obecnością i coraz większym zaufaniem.', category: 'Ciało' },
      { instruction: 'Pozwalam sobie iść głębiej w tempie, które jest dla mnie właściwe.', cue: 'Oddech, zanim przejdziesz dalej.' },
      { instruction: 'Widzę młodszą część siebie. Otaczam ją dziś obecnością, czułością i troską.', category: 'Wewnętrzne Dziecko' },
      { instruction: 'To, co wydarzyło się wtedy, zasługuje dziś na moje współczucie, zrozumienie i opiekę.', category: 'Wewnętrzne Dziecko' },
      { instruction: 'Moja wartość jest częścią mnie. Wracam do niej coraz pełniej.', category: 'Odzyskiwanie Mocy' },
      { instruction: 'Odzyskuję swoją energię, swój głos, swoje granice i prawo do wyboru.', category: 'Odzyskiwanie Mocy' },
      { instruction: 'Uwalniam historie, które pomniejszały moje poczucie własnej wartości, i wybieram widzieć siebie z większą prawdą i miłością.', category: 'Dusza' },
      { instruction: 'Jestem kimś więcej niż to, co mnie zraniło. Jest we mnie życie, mądrość, siła i zdolność do tworzenia nowego doświadczenia.', category: 'Dusza' },
    ],
  },
}

/**
 * Polish for the five suggestion reasons returned by suggestPreparation().
 * Keyed by the English string so the rule logic in preparations.ts stays the
 * single source of truth for WHICH practice is suggested — this only translates
 * the sentence explaining why.
 */
export const SUGGESTION_REASONS_PL: Record<string, string> = {
  'Ground yourself before you begin.': 'Ugruntuj się, zanim zaczniesz.',
  'Integrate what you just worked through.': 'Daj sobie chwilę na integrację tego, co właśnie się wydarzyło.',
  'Let the work continue through sound.': 'Pozwól tej pracy trwać dalej poprzez dźwięk.',
  'Discharge what\'s been building before you go back in.': 'Daj upust temu, co mogło się nagromadzić, zanim wejdziesz głębiej.',
  'Settle your nervous system before the next session.': 'Chwila na wyciszenie przed kolejną sesją.',
}

/**
 * Returns the practice with its user-visible copy swapped for the requested
 * language. Timings, colors and the tier-gating counts pass through untouched,
 * because those are behaviour rather than words. Steps merge positionally and
 * only where the Polish array is long enough, so a short translation leaves the
 * remaining English steps intact rather than truncating a regulation practice
 * partway through.
 */
export function localizePreparation(prep: Preparation, lang?: string): Preparation {
  if (lang !== 'pl') return prep

  const pl = PREPARATIONS_PL[prep.id]
  if (!pl) return prep

  return {
    ...prep,
    name: pl.name,
    tagline: pl.tagline,
    description: pl.description,
    regulation: pl.regulation,
    completionLine: pl.completionLine,
    attribution: pl.attribution ?? prep.attribution,
    steps: prep.steps.map((step, i) => {
      const t = pl.steps[i]
      if (!t) return step
      return {
        ...step,
        instruction: t.instruction,
        // Only override cue/category when the Polish supplies one — leaving the
        // English value would be worse than leaving the field absent.
        ...(t.cue !== undefined ? { cue: t.cue } : step.cue !== undefined ? { cue: step.cue } : {}),
        ...(t.category !== undefined ? { category: t.category } : {}),
      }
    }),
  }
}

/** Translates a suggestion reason, falling back to the English sentence. */
export function localizeSuggestionReason(reason: string, lang?: string): string {
  if (lang !== 'pl') return reason
  return SUGGESTION_REASONS_PL[reason] ?? reason
}
